import { tokens } from "../theme";
import { Box, useTheme } from "@mui/material";

// Drag handle for the bottom edge of a resizable element (e.g. IframeViewer).
// The vertical sibling of DrawerResizeHandle, with one deliberate difference:
// DrawerResizeHandle overlaps its drawer via absolute positioning, which is
// fine over ordinary DOM content — but here the content is an <iframe>, a
// separate browsing context that captures hover/pointer events for the
// pixels it renders regardless of a sibling's z-index. So this is a normal
// (non-absolute) flex item instead: it claims its own dedicated strip of
// space below the frame rather than sitting on top of it, which is the only
// reliable way to keep it hoverable/grabbable next to an iframe.
//
// Purely presentational + keyboard support; all state/math/persistence lives
// in useResizableHeight. Spread its handleProps directly onto this. Renders
// nothing when `enabled` is false (e.g. while the frame is maximized —
// already fullscreen, nothing to resize).
const FrameResizeHandle = ({
  enabled = true,
  height,
  minHeight,
  maxHeight,
  isDragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onKeyDown,
  ariaLabel = "Resize frame height",
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const ACCENT = colors.link;
  if (!enabled) return null;
  return (
    <Box
      role="separator"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(height)}
      aria-valuemin={Math.round(minHeight)}
      aria-valuemax={Math.round(maxHeight)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
      sx={{
        flexShrink: 0,
        height: "10px",
        cursor: "row-resize",
        touchAction: "none", // stop touch browsers hijacking this as a scroll gesture
        backgroundColor: isDragging ? `${ACCENT}47` : colors.divider,
        borderTop: `1px solid ${colors.border}`,
        // A little grip mark so the strip reads as draggable at a glance,
        // not just as a stray divider line.
        backgroundImage: `radial-gradient(circle, ${colors.muted} 1px, transparent 1px)`,
        backgroundSize: "6px 4px",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        "&:hover": { backgroundColor: `${ACCENT}29` },
        "&:focus-visible": {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: "-2px",
          backgroundColor: `${ACCENT}29`,
        },
      }}
    />
  );
};

export default FrameResizeHandle;
